import React, { createContext, useState, useEffect, useContext } from "react";
import { axiosInstance } from "../utils/axios";

// GraphQL query to get all spaces including default flag
const GET_SPACES = `
  query {
    getSpaces {
      id
      name
      default
    }
  }
`;

// GraphQL query to get default space
const GET_DEFAULT_SPACE = `
  query {
    getDefaultSpace {
      id
      name
      default
    }
  }
`;

export const SpaceContext = createContext();

export const SpaceProvider = ({ children }) => {
  const [spaces, setSpaces] = useState([]);
  const [currentSpace, setCurrentSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Function to fetch all spaces
  const fetchSpaces = async () => {
    try {
      console.log("Fetching spaces...");
      setLoading(true);

      const response = await axiosInstance.post("/graphql", {
        query: GET_SPACES,
      });

      console.log("API response:", response);

      // Check if response has data
      if (!response.data) {
        throw new Error("Invalid API response structure");
      }

      // Check if there are GraphQL errors
      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message);
      }

      // Ensure we have the correct data structure
      if (!response.data || !response.data.getSpaces) {
        console.warn("Unexpected API response structure:", response.data);
        return [];
      }

      const fetchedSpaces = response.data.getSpaces || [];
      console.log("Fetched spaces:", fetchedSpaces);

      setSpaces(fetchedSpaces);
      return fetchedSpaces;
    } catch (err) {
      console.error("Error fetching spaces:", err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Function to get the default space
  const fetchDefaultSpace = async () => {
    try {
      console.log("Fetching default space...");
      const response = await axiosInstance.post("/graphql", {
        query: GET_DEFAULT_SPACE,
      });

      console.log("Default space response:", response);

      // Check if there are GraphQL errors
      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message);
      }

      // Ensure we have the correct data structure
      if (!response.data) {
        console.warn("Unexpected API response structure for default space");
        return null;
      }

      return response.data.getDefaultSpace;
    } catch (err) {
      console.error("Error fetching default space:", err);
      return null;
    }
  };

  // Initialize - fetch spaces and set current space
  useEffect(() => {
    const initializeSpaces = async () => {
      try {
        console.log("Initializing spaces...");
        setLoading(true);

        // Get all spaces
        const allSpaces = await fetchSpaces();

        if (allSpaces.length === 0) {
          console.log("No spaces found from API");
          setInitialized(true);
          return;
        }

        // Try to get stored space ID from localStorage
        let storedSpaceId;
        try {
          storedSpaceId = localStorage.getItem("currentSpaceId");
          console.log("Stored space ID from localStorage:", storedSpaceId);
        } catch (err) {
          console.warn("Error accessing localStorage:", err);
          storedSpaceId = null;
        }

        if (storedSpaceId) {
          // If there's a stored space ID, find that space in the list
          const storedSpace = allSpaces.find(
            (space) => space.id === storedSpaceId
          );
          if (storedSpace) {
            console.log(
              "Setting current space from localStorage:",
              storedSpace
            );
            setCurrentSpace(storedSpace);
            setInitialized(true);
            return;
          } else {
            console.log("Stored space not found in available spaces");
          }
        }

        // If no stored space or stored space not found, try to get default space
        let defaultSpace = allSpaces.find((space) => space.default);
        console.log("Default space from list:", defaultSpace);

        // If no default space found in the list, try to fetch it directly
        if (!defaultSpace) {
          defaultSpace = await fetchDefaultSpace();
          console.log("Default space from API:", defaultSpace);
        }

        // If default space found, set it as current
        if (defaultSpace) {
          console.log("Setting default space as current:", defaultSpace);
          setCurrentSpace(defaultSpace);
          try {
            localStorage.setItem("currentSpaceId", defaultSpace.id);
          } catch (err) {
            console.warn("Error setting localStorage:", err);
          }
        } else if (allSpaces.length > 0) {
          // If no default space but spaces exist, use the first one
          console.log("No default space, using first space:", allSpaces[0]);
          setCurrentSpace(allSpaces[0]);
          try {
            localStorage.setItem("currentSpaceId", allSpaces[0].id);
          } catch (err) {
            console.warn("Error setting localStorage:", err);
          }
        }
      } catch (err) {
        console.error("Error initializing spaces:", err);
        setError(err.message);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    initializeSpaces();
  }, []);

  // Function to change the current space
  const changeSpace = (spaceId) => {
    console.log("Changing space to:", spaceId);
    const space = spaces.find((s) => s.id === spaceId);
    if (space) {
      console.log("Found space:", space);
      setCurrentSpace(space);
      try {
        localStorage.setItem("currentSpaceId", space.id);
      } catch (err) {
        console.warn("Error setting localStorage:", err);
      }
    } else {
      console.warn("Space not found for ID:", spaceId);
    }
  };

  // Refresh spaces list
  const refreshSpaces = async () => {
    console.log("Refreshing spaces...");
    const refreshedSpaces = await fetchSpaces();

    // If we have spaces but no current space, set the first one
    if (refreshedSpaces.length > 0 && !currentSpace) {
      // Try to find default space first
      const defaultSpace = refreshedSpaces.find((space) => space.default);
      if (defaultSpace) {
        console.log("Setting default space after refresh:", defaultSpace);
        setCurrentSpace(defaultSpace);
        try {
          localStorage.setItem("currentSpaceId", defaultSpace.id);
        } catch (err) {
          console.warn("Error setting localStorage:", err);
        }
      } else {
        // Otherwise use first space
        console.log("Setting first space after refresh:", refreshedSpaces[0]);
        setCurrentSpace(refreshedSpaces[0]);
        try {
          localStorage.setItem("currentSpaceId", refreshedSpaces[0].id);
        } catch (err) {
          console.warn("Error setting localStorage:", err);
        }
      }
    }

    return refreshedSpaces;
  };

  const contextValue = {
    spaces,
    currentSpace,
    loading,
    error,
    initialized,
    changeSpace,
    refreshSpaces,
  };

  console.log("SpaceContext state:", {
    spacesCount: spaces.length,
    currentSpaceId: currentSpace?.id || "none",
    loading,
    initialized,
  });

  return (
    <SpaceContext.Provider value={contextValue}>
      {children}
    </SpaceContext.Provider>
  );
};

// Custom hook to use the space context
export const useSpace = () => {
  const context = useContext(SpaceContext);
  if (!context) {
    throw new Error("useSpace must be used within a SpaceProvider");
  }
  return context;
};

export default SpaceContext;
